/**
 * Custom hook for managing chat connection and participant updates
 *
 * This hook handles:
 * - Initializing chat document if it doesn't exist
 * - Updating participants in Firestore
 * - Managing connection state
 */

import { useEffect, useRef, useState } from 'react';
import { Participant } from '../types';
import { DamageCaseFirebaseClient } from '../../../utils/damageCaseFirebase';

interface UseChatConnectionOptions {
    chatToken: string | number | null;
    participants: Participant[];
    firebaseClient: DamageCaseFirebaseClient | null;
    enabled?: boolean;
}

interface UseChatConnectionReturn {
    isInitialized: boolean;
    error: Error | null;
}

/**
 * Hook to manage chat document initialization and participant updates
 */
export function useChatConnection({
    chatToken,
    participants,
    firebaseClient,
    enabled = true,
}: UseChatConnectionOptions): UseChatConnectionReturn {
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const initializedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!enabled || !chatToken || !firebaseClient) {
            return;
        }

        const initializeChat = async () => {
            try {
                const tokenString = String(chatToken);
                const chatRef = firebaseClient.firestore.collection('chats').doc(tokenString);
                const doc = await chatRef.get();

                // Normalize incoming participants (support user_email/user_id/user_label)
                const normalize = (p: Participant): Participant => {
                    const email = (p as any)?.email || (p as any)?.user_email || '';
                    const id = (p as any)?.id || (p as any)?.user_id;
                    const label = (p as any)?.label || (p as any)?.user_label || email;
                    const is_alpc = email.startsWith('alpc_') ? true : p.is_alpc;
                    const is_acm = email.startsWith('acm_') ? true : p.is_acm;
                    return { email, id, label, is_alpc, is_acm };
                };

                const normalizedParticipants = participants
                    .map(normalize)
                    .filter(p => !!p.email);

                // Debug logs to verify incoming participants
                // eslint-disable-next-line no-console
                // console.log('[useChatConnection] incoming participants', normalizedParticipants);

                const sanitize = (p: Participant): Participant => {
                    const email = p.email;
                    const id = p.id;
                    const label = p.label;
                    const is_alpc = email?.startsWith('alpc_') ? true : p.is_alpc;
                    const is_acm = email?.startsWith('acm_') ? true : p.is_acm;
                    return {
                        email,
                        id,
                        label,
                        is_alpc,
                        is_acm,
                    };
                };

                if (!doc.exists) {
                    // Add is_alpc: true flag to all participants when initializing from ALPC
                    const participantsWithFlag = normalizedParticipants.map(p => ({
                        ...p,
                        is_alpc: p.is_alpc || p.email.startsWith('alpc_'),
                        is_acm: p.is_acm || p.email.startsWith('acm_'),
                    }));

                    // Initialize chat document with empty messages and participants
                    await chatRef.set({
                        messages: [],
                        participants: participantsWithFlag,
                    });
                } else {
                    // Update participants if new ones are added
                    const existingData = doc.data();
                    const existingParticipants = (existingData?.participants || []) as Participant[];

                    // eslint-disable-next-line no-console
                    // console.log('[useChatConnection] existing participants', existingParticipants);

                    // Check if we have new participants
                    const existingEmails = new Set(
                        existingParticipants
                            .filter(p => p.email)
                            .map(p => p.email.toLowerCase())
                    );
                    const newParticipants = normalizedParticipants.filter(
                        p => p.email && !existingEmails.has(p.email.toLowerCase())
                    );

                    // eslint-disable-next-line no-console
                    // console.log('[useChatConnection] new participants to add', newParticipants);

                    if (newParticipants.length > 0) {
                        // Add is_alpc: true flag to new participants added from ALPC
                        const newParticipantsWithFlag = newParticipants.map(p => ({
                            ...p,
                            is_alpc: p.is_alpc || p.email.startsWith('alpc_'),
                            is_acm: p.is_acm || p.email.startsWith('acm_'),
                        }));

                        // Merge participants, ensuring no duplicate emails
                        const mergedParticipants = [
                            ...existingParticipants,
                            ...newParticipantsWithFlag.filter(
                                p => !existingParticipants.some(
                                    ep => ep.email?.toLowerCase() === p.email?.toLowerCase()
                                )
                            ),
                        ];

                        await chatRef.update({
                            participants: mergedParticipants,
                        });
                    }
                }

                setIsInitialized(true);
                initializedRef.current = true;
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Failed to initialize chat'));
                setIsInitialized(false);
            }
        };

        initializeChat();
    }, [chatToken, enabled, participants, firebaseClient]);

    return { isInitialized, error };
}

